// iter184 — Trip Summary Page
// Full-page route /groups/:groupId/trip-summary
// Renders the comprehensive trip dashboard with Per-Member, Categories, Highlights.
// Includes Export to PDF and "Send via WhatsApp" actions.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, MessageCircle, Loader2, TrendingUp, TrendingDown, Equal, Plane, Bed, ChartBar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const formatNXC = (n) => `${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} ₹`;
const formatPct = (n) => `${(n || 0).toFixed(1)}%`;

export default function TripSummaryPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [waSending, setWaSending] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/groups/${groupId}/trip-summary`);
      setSummary(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load trip summary');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const downloadPdf = async (which = 'summary') => {
    setDownloading(true);
    try {
      const path = which === 'full'
        ? `/groups/${groupId}/export/trip-pdf`
        : `/groups/${groupId}/export/trip-summary-pdf`;
      const r = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${summary?.group?.name || 'trip'}_${which === 'full' ? 'report' : 'summary'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const sendWhatsApp = async () => {
    const phone = (waPhone || '').replace(/\D/g, '');
    if (phone.length < 10) { toast.error('Enter a 10-digit phone number'); return; }
    setWaSending(true);
    try {
      const r = await api.post(`/groups/${groupId}/trip-summary/whatsapp`, { phone });
      toast.success(r.data?.message || 'Sent via WhatsApp');
      setWaPhone('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send via WhatsApp');
    } finally {
      setWaSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="trip-summary-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!summary) return null;
  const g = summary.group;
  const t = summary.totals;
  const ts = summary.top_stats;

  return (
    <div className="max-w-5xl lg:max-w-none mx-auto px-4 py-4 space-y-4" data-testid="trip-summary-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/groups/${groupId}`)}
          data-testid="back-to-group-btn"
          className="rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl truncate" data-testid="trip-name">{g.name}</h1>
          <p className="text-xs text-muted-foreground">
            {g.trip_start_date && g.trip_end_date ? (
              <>{String(g.trip_start_date).slice(0, 10)} → {String(g.trip_end_date).slice(0, 10)} · {g.total_nights} nights · {g.member_count} members</>
            ) : (
              <>{g.member_count} members</>
            )}
          </p>
        </div>
      </div>

      {/* Hero metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hero-metrics">
        <Metric label="Total spent" value={formatNXC(t.grand_total)} testid="metric-total" highlight />
        <Metric label="Settles up" value={formatNXC(t.total_real)} testid="metric-real" />
        <Metric label="Direct paid" value={formatNXC(t.total_direct)} testid="metric-direct" />
        <Metric label="Expenses" value={`${ts.expense_count} + ${ts.direct_payments_count}`} testid="metric-count" sub={`${ts.direct_payments_count} direct`} />
      </div>

      {/* Export + WhatsApp */}
      <div className="flex flex-wrap gap-2 p-3 rounded-xl border-2 border-border bg-card" data-testid="export-bar">
        <Button onClick={() => downloadPdf('summary')} disabled={downloading} data-testid="dl-summary-pdf" className="rounded-full h-9 text-xs font-bold uppercase">
          <Download className="w-4 h-4 mr-1.5" />Summary PDF
        </Button>
        <Button onClick={() => downloadPdf('full')} disabled={downloading} variant="outline" data-testid="dl-full-pdf" className="rounded-full h-9 text-xs font-bold uppercase border-2">
          <Download className="w-4 h-4 mr-1.5" />Full Report PDF
        </Button>
        <div className="flex items-center gap-1.5 ml-auto">
          <input
            value={waPhone}
            onChange={(e) => setWaPhone(e.target.value)}
            placeholder="WA phone (10-digit)"
            data-testid="wa-phone-input"
            className="h-9 px-3 rounded-full border-2 border-border text-xs w-44"
            inputMode="numeric"
          />
          <Button onClick={sendWhatsApp} disabled={waSending || !waPhone} data-testid="send-wa-btn" variant="outline" className="rounded-full h-9 text-xs font-bold uppercase border-2 border-green-500/50 text-green-700">
            <MessageCircle className="w-4 h-4 mr-1.5" />Send
          </Button>
        </div>
      </div>

      {/* Per-Member Settlement */}
      <Section title="Per-Member Settlement" icon={<ChartBar className="w-4 h-4" />} testid="section-members">
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground bg-muted/50">
              <tr>
                <th className="p-2 text-left">Member</th>
                <th className="p-2 text-right">Paid</th>
                <th className="p-2 text-right">Fair Share</th>
                <th className="p-2 text-right">Net</th>
                <th className="p-2 text-right hidden sm:table-cell">Nights</th>
                <th className="p-2 text-right hidden sm:table-cell">Cost/Night</th>
              </tr>
            </thead>
            <tbody>
              {summary.members.map((m) => (
                <tr key={m.user_id} className="border-t border-border" data-testid={`member-row-${m.user_id}`}>
                  <td className="p-2 font-semibold">{m.name}<div className="text-xs text-muted-foreground sm:hidden">{m.nights || 0}n · {m.cost_per_night ? formatNXC(m.cost_per_night) + '/n' : '—'}</div></td>
                  <td className="p-2 text-right tabular-nums">{formatNXC(m.paid)}</td>
                  <td className="p-2 text-right tabular-nums">{formatNXC(m.share)}</td>
                  <td className={`p-2 text-right tabular-nums font-bold ${m.net > 0 ? 'text-green-700' : m.net < 0 ? 'text-red-600' : ''}`}>
                    <span className="inline-flex items-center gap-1">
                      {m.net > 0 ? <TrendingUp className="w-3 h-3" /> : m.net < 0 ? <TrendingDown className="w-3 h-3" /> : <Equal className="w-3 h-3" />}
                      {formatNXC(Math.abs(m.net))}
                    </span>
                  </td>
                  <td className="p-2 text-right tabular-nums hidden sm:table-cell">{m.nights || '—'}</td>
                  <td className="p-2 text-right tabular-nums hidden sm:table-cell">{m.cost_per_night ? formatNXC(m.cost_per_night) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          <strong>Net</strong>: + means group owes them back, - means they owe the group. Settlements below show the optimal payments.
        </p>
      </Section>

      {/* Category Breakdown */}
      <Section title="Category Breakdown" icon={<ChartBar className="w-4 h-4" />} testid="section-categories">
        {summary.categories.length === 0 && (
          <p className="text-xs text-muted-foreground">No approved expenses yet.</p>
        )}
        <div className="space-y-2">
          {summary.categories.map((c) => (
            <div key={c.category} data-testid={`category-row-${c.category}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="capitalize font-semibold">{c.category}</span>
                <span className="tabular-nums text-xs">{formatNXC(c.total)} <span className="text-muted-foreground">· {formatPct(c.percent)} · {c.expense_count} exp</span></span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${Math.min(100, c.percent)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Highlights */}
      <Section title="Highlights" icon={<TrendingUp className="w-4 h-4" />} testid="section-highlights">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ts.biggest_expense && (
            <Highlight label="💸 Biggest expense" testid="hl-biggest">
              <strong>{ts.biggest_expense.description}</strong> · {formatNXC(ts.biggest_expense.amount)}
              <div className="text-xs text-muted-foreground capitalize">{ts.biggest_expense.category} · {ts.biggest_expense.paid_by_name}</div>
            </Highlight>
          )}
          {ts.top_spender && (
            <Highlight label="🏆 Top contributor" testid="hl-top-spender">
              <strong>{ts.top_spender.name}</strong> fronted {formatNXC(ts.top_spender.paid)}
            </Highlight>
          )}
          {ts.top_consumer && (
            <Highlight label="📊 Highest fair-share" testid="hl-top-consumer">
              <strong>{ts.top_consumer.name}</strong> · {formatNXC(ts.top_consumer.share)}
            </Highlight>
          )}
          {ts.most_expensive_day && (
            <Highlight label="📅 Most expensive day" testid="hl-expensive-day">
              <strong>{ts.most_expensive_day.date}</strong> · {formatNXC(ts.most_expensive_day.total)}
            </Highlight>
          )}
          {!ts.biggest_expense && !ts.top_spender && !ts.top_consumer && !ts.most_expensive_day && (
            <p className="text-xs text-muted-foreground col-span-2">Add approved expenses to see highlights.</p>
          )}
        </div>
      </Section>

      {/* Rooms (only if rooms defined) */}
      {summary.rooms.length > 0 && (
        <Section title="Rooms Allocation" icon={<Bed className="w-4 h-4" />} testid="section-rooms">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {summary.rooms.map((r) => (
              <div key={r.id} className="p-3 rounded-xl border-2 border-border" data-testid={`room-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{r.label}</span>
                  <span className="text-xs tabular-nums">{formatNXC(r.total_spent)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {r.occupants.map((o) => o.name).join(', ')} · {r.occupant_count} occupants
                  {r.total_spent > 0 && (<> · <strong>{formatNXC(r.per_occupant)}</strong>/person</>)}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Direct Payments separate */}
      {t.total_direct > 0 && (
        <Section title="Direct Payments (Agent-Booked)" icon={<Plane className="w-4 h-4" />} testid="section-direct">
          <p className="text-xs text-muted-foreground mb-2">
            Total <strong>{formatNXC(t.total_direct)}</strong> across {ts.direct_payments_count} entries — each member paid the vendor directly. No settlement impact.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {summary.members.filter((m) => m.direct_paid > 0).map((m) => (
              <div key={m.user_id} className="p-2.5 rounded-xl border-2 border-border" data-testid={`direct-paid-${m.user_id}`}>
                <p className="text-xs font-semibold">{m.name}</p>
                <p className="text-sm tabular-nums">{formatNXC(m.direct_paid)}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Settlements Plan */}
      {summary.settlements_plan.length > 0 && (
        <Section title="Who Pays Whom" icon={<TrendingDown className="w-4 h-4" />} testid="section-settlements">
          <div className="space-y-2">
            {summary.settlements_plan.map((p, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2.5 rounded-xl bg-primary/5 border border-primary/20" data-testid={`settle-row-${idx}`}>
                <span className="text-sm flex-1"><strong>{p.from}</strong> → <strong>{p.to}</strong></span>
                <span className="font-bold tabular-nums">{formatNXC(p.amount)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Metric({ label, value, sub, testid, highlight }) {
  return (
    <div className={`p-3 rounded-xl border-2 ${highlight ? 'border-primary bg-primary/5' : 'border-border bg-card'}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${highlight ? 'text-primary' : ''}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <section className="p-3 sm:p-4 rounded-2xl border-2 border-border bg-card" data-testid={testid}>
      <h2 className="font-display text-base flex items-center gap-1.5 mb-2.5">
        {icon}{title}
      </h2>
      {children}
    </section>
  );
}

function Highlight({ label, children, testid }) {
  return (
    <div className="p-2.5 rounded-xl border-2 border-border bg-card" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
      <div className="text-sm mt-0.5">{children}</div>
    </div>
  );
}
